import { Link } from '@/components/Link';
import { DashboardHeader } from '../../components/DashboardHeader';
import { DashboardSection } from '../../components/DashboardSection';
import { PlatformMark } from '../../components/PlatformMark';
import { PriceTag } from '../../components/PriceTag';
import { useCatalogLinks } from '../../components/CatalogLinks';
import type { ApiDetail, CategoryId, Endpoint } from '../../features/catalog/model';
import { cheapest } from '../../lib/catalog';
import { m } from '../../paraglide/messages';

export interface DashboardApiDetailBodyProps {
  detail: ApiDetail;
  /** the platform's fallback HugeIcons name from the catalog list */
  icon: string;
  /** the platform's endpoints in the page's established order */
  endpoints: Endpoint[];
}

/**
 * A platform inside the dashboard: who it is, and what you can call. No
 * benchmark band, tools shelf, FAQ, About prose or funnel banner - a customer
 * who is signed in came to find an endpoint, not to be sold the platform.
 *
 * The price on each row is `cheapest`, the same serving-lane rule every other
 * surface uses, and the hrefs come from `useCatalogLinks`, so browsing from here
 * stays inside the dashboard.
 */
export const DashboardApiDetailBody = ({
  detail,
  icon,
  endpoints,
}: DashboardApiDetailBodyProps) => {
  const links = useCatalogLinks();
  const single = endpoints.every((e) => e.providers.length === 1);
  return (
    <div className="flex flex-col gap-8">
      <DashboardHeader
        crumb={
          <Link to={links.catalog} className="hover:text-text">
            {m.platform_catalog()}
          </Link>
        }
        title={
          <span className="flex items-center gap-2.5">
            <PlatformMark
              id={detail.id}
              icon={icon}
              category={detail.category as CategoryId}
              size={30}
              iconSize={17}
            />
            {detail.name}
          </span>
        }
        description={detail.blurb}
      />

      <DashboardSection
        title={m.platform_header_endpoints()}
        description={
          single ? m.app_catalog_endpoints_single() : m.app_catalog_endpoints_multi()
        }
      >
        <div className="divide-y divide-rule border-t border-rule">
          {endpoints.map((endpoint) => (
            <EndpointLine key={endpoint.id} apiId={detail.id} endpoint={endpoint} />
          ))}
        </div>
      </DashboardSection>
    </div>
  );
};

/** One ruled endpoint row: method and name, the mono path, and the price. */
const EndpointLine = ({ apiId, endpoint }: { apiId: string; endpoint: Endpoint }) => {
  const links = useCatalogLinks();
  const cheap = cheapest(endpoint);
  return (
    <Link
      to={links.endpoint(apiId, endpoint.id)}
      className="flex items-start justify-between gap-4 py-3 transition-colors hover:bg-wash"
    >
      <span className="min-w-0">
        <span className="flex flex-wrap items-baseline gap-2">
          <span className="text-14 font-medium text-text">{endpoint.action}</span>
          <span className="mono text-12 text-text-3">
            {endpoint.method} {endpoint.path}
          </span>
        </span>
        <span className="mt-1 block text-13 text-text-2">{endpoint.description}</span>
      </span>
      <PriceTag
        price={cheap.price}
        size="sm"
        label={
          endpoint.providers.length === 1 ? m.platform_row_price_label() : m.platform_row_from_label()
        }
        className="shrink-0"
      />
    </Link>
  );
};
